import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../axios/axios";
import { checkRegisteredUser } from "../utils/helper";
import { fullName, getPathEnv } from "../helper";
import Loading from "../Components/Loading";
import "./FreelanceIndividual.css";

const FreelanceIndividual = () => {
  const navigate = useNavigate();
  const { freelancerId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [freelancer, setFreelancer] = useState(null);
  const isLoggedIn = checkRegisteredUser();

  const fetchData = async () => {
    try {
    setIsLoading(true);
      const response = await axiosInstance.get(
        `/unauth/freelancer/mobile/${freelancerId}`
    );
      if (response.data && response.data.data && response.data.data.data) {
        setFreelancer(response.data.data.data);
    }
    } catch (error) {
      console.error("Error fetching freelancer:", error);
      setFreelancer(null);
    } finally {
    setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [freelancerId]);

  if (isLoading) {
    return (
      <div className="freelancer-individual-loading">
        <Loading />
      </div>
    );
  }

  if (!freelancer) {
    return (
      <div className="freelancer-individual-container">
        <div className="freelancer-individual-error">
          <h3>Freelancer not found</h3>
          <button onClick={() => navigate(-1)} className="btn-primary">Go Back</button>
        </div>
      </div>
    );
  }

  const experience = freelancer?.experience;
  const eConsultation = freelancer?.eConsultation;
  const isPublished = experience?.publish === "yes";

  const handleViewResume = () => {
    if (!isLoggedIn) {
      alert("Please login to view the resume");
      navigate("/login");
      return;
    }
    if (!freelancer?.resume?.path) {
      alert("There is no resume for this freelancer");
      return;
    }
    window.open(getPathEnv(freelancer?.resume?.path), "_blank");
  };

  const handleViewProfilePhoto = () => {
    if (freelancer?.profilePhoto?.path) {
      window.open(getPathEnv(freelancer?.profilePhoto?.path), "_blank");
    }
  };

  return (
    <div className="freelancer-individual-container">
      {/* Header Section */}
      <div className="freelancer-header">
        <button
          onClick={() => navigate(-1)}
          className="back-button"
        >
          ← Go Back
        </button>
        <div className="freelancer-status-badges">
          {isPublished && <span className="status-tag status-tag-success">Published</span>}
          {freelancer?.isApproved && <span className="status-tag status-tag-success">Approved</span>}
          {freelancer?.isFeatured && <span className="status-tag status-tag-featured">Featured ⭐</span>}
          {freelancer?.emailVerified && <span className="status-tag status-tag-success">Email Verified</span>}
          {freelancer?.Iam?.map((role, index) => (
            <span 
              key={index} 
              className={`status-tag ${
                role === "Trainer" ? "status-tag-orange" : 
                role === "Consultant" ? "status-tag-gold" : 
                "status-tag-blue"
              }`}
            >
              {role}
            </span>
          ))}
        </div>
      </div>

      {/* Main Profile Section */}
      <div className="freelancer-profile-card">
        <div className="freelancer-profile-header">
          <div className="profile-photo-section">
            {freelancer?.profilePhoto?.path ? (
              <img
                src={getPathEnv(freelancer.profilePhoto.path)}
                alt="Profile"
                onClick={handleViewProfilePhoto}
                className="profile-photo"
                style={{ cursor: "pointer" }}
              />
            ) : (
              <div className="profile-photo-placeholder">👤</div>
            )}
            {freelancer?.isFeatured && (
              <div className="featured-badge">
                ⭐ Featured
              </div>
            )}
          </div>
          <div className="profile-info-section">
            <h2 className="freelancer-name">
              {freelancer?.name || "Freelancer"}
            </h2>
            {freelancer?.legalName && (
              <p className="legal-name">{freelancer.legalName}</p>
            )}
            {freelancer?.profileHeader && (
              <p className="profile-header">{freelancer.profileHeader}</p>
            )}
            {freelancer?.companyName && (
              <p className="company-name">Company: {freelancer.companyName}</p>
            )}
            <div className="profile-links">
              {freelancer?.linkedinLink && (
                <a
                  href={
                    freelancer.linkedinLink.startsWith("http")
                      ? freelancer.linkedinLink
                      : `https://${freelancer.linkedinLink}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="profile-link"
                >
                  🔗 LinkedIn
                </a>
              )}
              {freelancer?.personalLink && (
                <a
                  href={
                    freelancer.personalLink.startsWith("http")
                      ? freelancer.personalLink
                      : `https://${freelancer.personalLink}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="profile-link"
                >
                  🌐 Website
                </a>
              )}
            </div>
          </div>
          <div className="profile-stats-section">
            {isLoggedIn && experience?.hourlyRate && (
              <div className="stat-item">
                <span className="stat-icon">💰</span>
                <div>
                  <div className="stat-value">{experience.hourlyRate}</div>
                  <div className="stat-label">Hourly Rate</div>
                </div>
              </div>
            )}
            {experience?.totalExperience && (
              <div className="stat-item">
                <span className="stat-icon">📅</span>
                <div>
                  <div className="stat-value">{experience.totalExperience}</div>
                  <div className="stat-label">Experience</div>
                </div>
              </div>
            )}
            {isLoggedIn && freelancer?.appliedVacanciesCount !== undefined && (
              <div className="stat-item">
                <span className="stat-icon">📄</span>
                <div>
                  <div className="stat-value">{freelancer.appliedVacanciesCount}</div>
                  <div className="stat-label">Applications</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="freelancer-content-grid">
        {/* Left Column */}
        <div className="freelancer-left-column">
          {/* Profile Summary Section */}
          {freelancer?.profileSummary && (
            <div className="freelancer-section-card">
              <div className="freelancer-card-header">
                <h3>Profile Summary</h3>
              </div>
              <div className="freelancer-card-body">
                <p className="profile-summary">
                {freelancer.profileSummary}
                </p>
              </div>
            </div>
          )}

          {/* Skills Section */}
          {freelancer?.skills && freelancer.skills.length > 0 && (
            <div className="freelancer-section-card">
              <div className="freelancer-card-header">
                <h3>Skills</h3>
              </div>
              <div className="freelancer-card-body">
              <div className="skills-container">
                {freelancer.skills.map((skill, index) => (
                    <span key={index} className="skill-tag">
                    {skill}
                    </span>
                ))}
                </div>
              </div>
            </div>
          )}

          {/* Experience Details Section */}
          <div className="freelancer-section-card">
            <div className="freelancer-card-header">
              <h3>Experience Details</h3>
            </div>
            <div className="freelancer-card-body">
            <div className="info-grid">
              <div className="info-item">
                  <strong>Total Experience:</strong>
                  <span>{experience?.totalExperience || "Not specified"}</span>
              </div>
              <div className="info-item">
                  <strong>Degree:</strong>
                  <span>{experience?.degree || "Not specified"}</span>
              </div>
              <div className="info-item">
                  <strong>Nationality:</strong>
                  <span>{experience?.nationality || "Not specified"}</span>
              </div>
              <div className="info-item">
                  <strong>Residency/Visa:</strong>
                  <span>{experience?.residencyVisa || "Not specified"}</span>
              </div>
              <div className="info-item">
                  <strong>First Language:</strong>
                  <span>{experience?.firstLanguage || "Not specified"}</span>
              </div>
              <div className="info-item">
                  <strong>Second Language:</strong>
                  <span>{experience?.secondLanguage || "Not specified"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Resume Download Section - only for authenticated users */}
          {isLoggedIn && freelancer?.resume?.path && (
            <div className="freelancer-section-card file-download-card">
              <div className="freelancer-card-body">
              <div className="file-download-container">
                  <span className="file-icon">📄</span>
                <div className="file-info">
                    <strong className="file-name">
                    {freelancer.resume.fileName || "Download Resume"}
                    </strong>
                    <span className="file-label">
                    View or download the freelancer's resume
                    </span>
                  </div>
                  <button
                    onClick={handleViewResume}
                    className={`download-button ${!isLoggedIn ? 'download-button-login' : ''}`}
                  >
                    {isLoggedIn ? '📥 View Resume' : '🔒 Login to View'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="freelancer-right-column">
          {/* Availability Section */}
          <div className="freelancer-section-card">
            <div className="freelancer-card-header">
              <h3>📅 Availability & Work Preferences</h3>
            </div>
            <div className="freelancer-card-body">
            <div className="info-grid">
              <div className="info-item">
                  <strong>Availability:</strong>
                  <span>{experience?.availability || "Not specified"}</span>
              </div>
              <div className="info-item">
                  <strong>Start Date:</strong>
                  <span>{experience?.startDate || "Not specified"}</span>
              </div>
              <div className="info-item">
                  <strong>Work Station:</strong>
                  <span>{experience?.workStation || "Not specified"}</span>
              </div>
              <div className="info-item">
                  <strong>Travel Willingness:</strong>
                  <span>{experience?.travelWillingness || "Not specified"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* User Contact Section */}
          <div className="freelancer-section-card">
            <div className="freelancer-card-header">
              <h3>👤 Contact Information</h3>
            </div>
            <div className="freelancer-card-body">
              <div className="contact-container">
                <div className="contact-item">
                  <span className="contact-icon">✉️</span>
                  <div>
                    <small>Email</small>
                    <div>
                      {isLoggedIn ? (
                        <a href={`mailto:${freelancer?.email}`}>{freelancer?.email || "Not specified"}</a>
                      ) : (
                        <span className="hidden-contact" onClick={() => navigate("/login")}>
                          ************
                          <span className="login-tooltip">Please login to view</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {/* {freelancer?.phone && (
                  <>
                    <hr style={{ margin: "12px 0", border: "none", borderTop: "1px solid #e5e7eb" }} />
                    <div className="contact-item">
                      <span className="contact-icon">📞</span>
                      <div>
                        <small>Phone</small>
                        <div>
                          {isLoggedIn ? (
                            <a href={`tel:${freelancer.phone}`}>{freelancer.phone}</a>
                          ) : (
                            <span className="hidden-contact" onClick={() => navigate("/login")}>
                              ************
                              <span className="login-tooltip">Please login to view</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )} */}
              </div>
            </div>
          </div>

          {/* E-Consultation Section - only for authenticated users */}
          {isLoggedIn && eConsultation && (
            <div className="freelancer-section-card">
              <div className="freelancer-card-header">
                <h3>🌐 E-Consultation Services</h3>
              </div>
              <div className="freelancer-card-body">
              <div className="info-grid">
                <div className="info-item">
                    <strong>Hourly Rate:</strong>
                    <span className="payment-value">
                    {eConsultation.hourlyRate || "Not specified"}
                    </span>
                </div>
                {eConsultation.timezone && (
                  <div className="info-item">
                      <strong>Timezone:</strong>
                      <span>{eConsultation.timezone}</span>
                  </div>
                )}
                <div className="info-item">
                    <strong>Send Conference Info:</strong>
                    <span>{eConsultation.sendConferenceInfo ? "Yes" : "No"}</span>
                </div>
                <div className="info-item">
                    <strong>Send Payment Info:</strong>
                    <span>{eConsultation.sendPaymentInfo ? "Yes" : "No"}</span>
                </div>
              </div>

              {/* Availability Schedule */}
              {eConsultation.availability && eConsultation.availability.length > 0 && (
                <div className="availability-schedule">
                    <strong style={{ display: "block", marginTop: "16px", marginBottom: "8px" }}>
                    Weekly Schedule:
                    </strong>
                  <div className="schedule-grid">
                    {eConsultation.availability.map((slot, index) => (
                      <div key={index} className="schedule-item">
                          <strong>{slot.day}:</strong>
                          <span>
                          {slot.from} - {slot.to}
                          </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* E-Consultation Messages */}
              {(eConsultation.WelcomeMessage ||
                eConsultation.ConferenceMessage ||
                eConsultation.PaymentMessage) && (
                <div className="consultation-messages">
                  {eConsultation.WelcomeMessage && (
                    <div className="message-section">
                        <strong style={{ display: "block", marginBottom: "8px" }}>
                        Welcome Message:
                        </strong>
                        <p className="message-text">{eConsultation.WelcomeMessage}</p>
                    </div>
                  )}
                  {eConsultation.ConferenceMessage && (
                    <div className="message-section">
                        <strong style={{ display: "block", marginBottom: "8px" }}>
                        Conference Message:
                        </strong>
                        <p className="message-text">{eConsultation.ConferenceMessage}</p>
                    </div>
                  )}
                  {eConsultation.PaymentMessage && (
                    <div className="message-section">
                        <strong style={{ display: "block", marginBottom: "8px" }}>
                        Payment Message:
                        </strong>
                        <p className="message-text">{eConsultation.PaymentMessage}</p>
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>
          )}

          {/* Statistics Section - only for authenticated users */}
          {isLoggedIn && (
            <div className="freelancer-section-card">
              <div className="freelancer-card-header">
                <h3>Statistics</h3>
              </div>
              <div className="freelancer-card-body">
              <div className="info-grid">
                  {freelancer?.appliedVacanciesCount !== undefined && (
                  <div className="info-item">
                      <strong>Applied Vacancies:</strong>
                      <span>{freelancer.appliedVacanciesCount}</span>
                  </div>
                )}
                  {freelancer?.postedVacanciesCount !== undefined && (
                  <div className="info-item">
                      <strong>Posted Vacancies:</strong>
                      <span>{freelancer.postedVacanciesCount}</span>
                  </div>
                )}
                  <div className="info-item">
                    <strong>Email Vacancies:</strong>
                    <span>{freelancer?.emailVacancies ? "Yes" : "No"}</span>
                  </div>
                  <div className="info-item">
                    <strong>Pay Revenue:</strong>
                    <span>{freelancer?.payRevenue ? "Yes" : "No"}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreelanceIndividual;
